import { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { Checkbox, HooksService, WithTooltip } from 'basic-components';
import { DragMarker, Info } from 'basic-components/icons';

import Draggable from 'react-draggable';
import { When } from 'react-if';

import classNames from 'classnames';
import './ColumnItem.scss';

const TOOLTIP_STYLES = { width: 150 }
const STARTING_POSITION = { y: 0, x: 0 }
const ColumnItem = ({ columnName, draggingIndex, hoveredIndex, isDragDisabled = false, isHidden = false, isNotAvailable = false, itemIndex, onDragAndDrop, setDraggingIndex, setHoveredIndex, swapIsHidden, isSelectedAsFilter = false }) => {
  const elementRef = useRef(),
        isDisabled = useMemo(() => isSelectedAsFilter || isNotAvailable, [isNotAvailable, isSelectedAsFilter]),
        { getTop, getTransition, onDrag, onDragStart, onDragStop } = HooksService.useDragAndDropAnimated({
          elementClassName: "ng-office-app__authed__content__results__column-selector__content__section__body__item",
          elementRef,
          setHoveredIndex,
          setDraggingIndex,
          onDragAndDrop,
          hoveredIndex,
          itemIndex
        }),
        onDragLocal = useCallback((event, data) => {
          onDrag(event, data)
          const content = elementRef.current?.closest('.ng-office-app__authed__content__results__column-selector__content'),
                contentRect = content?.getBoundingClientRect(),
                edgeSize = 48
          if(content && contentRect) {
            if(event.clientY < contentRect.top + edgeSize) {
              content.scrollTop -= 8
            } else if(event.clientY > contentRect.bottom - edgeSize) {
              content.scrollTop += 8
            }
          }
        }, [onDrag])

  return (
    <Draggable
        axis="y"
        bounds="parent"
        defaultClassNameDragging="ng-office-app__authed__content__results__column-selector__content__section__body__item__drag-marker--is-dragging"
        disabled={isDragDisabled || !onDragAndDrop}
        handle=".ng-office-app__authed__content__results__column-selector__content__section__body__item__drag-marker"
        nodeRef={elementRef}
        onDrag={onDragLocal}
        onStart={onDragStart}
        onStop={onDragStop}
        position={STARTING_POSITION}
        scale={1}
        zIndex={1}
    >
      <div 
          className={classNames(
            "ng-office-app__authed__content__results__column-selector__content__section__body__item",
            { "ng-office-app__authed__content__results__column-selector__content__section__body__item--is-hidden": isHidden || isDisabled },
          )}
          ref={elementRef}
          style={{ 
            top: getTop({ draggingIndex, itemIndex, hoveredIndex, withDragAndDrop: !!onDragAndDrop }),
            transition: getTransition({ draggingIndex, itemIndex }),
            zIndex: draggingIndex === itemIndex ? 100 : 99  - itemIndex
          }}
      >
        <div className={classNames(
          "ng-office-app__authed__content__results__column-selector__content__section__body__item__drag-marker",
          { "ng-office-app__authed__content__results__column-selector__content__section__body__item__drag-marker--is-disabled": isDragDisabled }
        )}>
          <DragMarker
              color={isDragDisabled ? "grey-base" : "grey-medium"}
              height={16}
              width={16}
          />
        </div>
        <div className="ng-office-app__authed__content__results__column-selector__content__section__body__item__label">
          {columnName}
        </div>
        <When condition={isNotAvailable || isSelectedAsFilter}>
          {() => (
            <WithTooltip
                data-place="top"
                extraStyle={TOOLTIP_STYLES}
                text={isSelectedAsFilter ? "Filtered column is automatically set as hidden" : "Column is not available in the current results"}
            >
              <div className="ng-office-app__authed__content__results__column-selector__content__section__body__item__info">
                <Info
                    color="blue-bright"
                    height={16}
                    width= {16}
                />
              </div>
            </WithTooltip>
          )}
        </When>
        <div className="ng-office-app__authed__content__results__column-selector__content__section__body__item__checkbox">
          <Checkbox
              checked={!isHidden && !isDisabled}
              disabled={isDisabled}
              name="isVisible"
              onChange={swapIsHidden}
          />
        </div>
      </div>
    </Draggable>
  )
}

ColumnItem.propTypes = {
  columnName: PropTypes.string.isRequired,
  draggingIndex: PropTypes.number,
  hoveredIndex: PropTypes.number,
  isDragDisabled: PropTypes.bool,
  isHidden: PropTypes.bool,
  isNotAvailable: PropTypes.bool,
  isSelectedAsFilter: PropTypes.bool,
  itemIndex: PropTypes.number.isRequired,
  onDragAndDrop: PropTypes.func,
  setDraggingIndex: PropTypes.func,
  setHoveredIndex: PropTypes.func,
  swapIsHidden: PropTypes.func.isRequired
}

export default ColumnItem;
